import { AlertTriangle, CheckSquare, ClipboardCheck, TrendingUp, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTaskList } from '../api/tasks';
import { useEngineer, useEngineerSignals } from '../api/engineers';
import { useCheckInHistory } from '../api/checkIns';
import PageHeader from '../components/layout/PageHeader';
import Badge from '../components/ui/Badge';
import { DashboardSkeleton } from '../components/ui/skeleton';
import ErrorState from '../components/ui/ErrorState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { formatDate, daysUntil } from '../lib/dates';
import type { TaskStatus } from '../types/api';

const STATUS_VARIANT: Record<TaskStatus, 'green' | 'red' | 'gray'> = {
  active:  'green',
  blocked: 'red',
  done:    'gray',
};

export default function DashboardPage() {
  const { currentUser } = useAuth();
  const id = currentUser!.id;

  const { data: engineer }          = useEngineer(id);
  const { data: tasks, isLoading, error, refetch } = useTaskList({ assigneeId: id });
  const { data: signals }           = useEngineerSignals(id);
  const { data: checkIns }          = useCheckInHistory(id);

  const activeTasks   = tasks?.items.filter((t) => t.status !== 'done') ?? [];
  const blockedTasks  = activeTasks.filter((t) => t.status === 'blocked');
  const totalPoints   = activeTasks.reduce((s, t) => s + t.points, 0);
  const baseline      = engineer?.baselinePoints ?? 0;
  const pct           = baseline > 0 ? Math.min((totalPoints / baseline) * 100, 150) : 0;

  const todayIso       = new Date().toISOString().slice(0, 10);
  const checkedInToday = checkIns?.items.some((c) => c.date === todayIso);
  const overworked     = signals?.isOverworked ?? false;
  const signalCount    = signals
    ? [signals.loadVsBaseline, signals.concurrent, signals.staleInProgress].filter((s) => s.tripped).length
    : 0;

  if (isLoading) return <DashboardSkeleton />;
  if (error)     return <ErrorState onRetry={refetch} />;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Good ${greeting()}, ${currentUser!.name.split(' ')[0]}`}
        description="Here's your workload at a glance."
      />

      {/* Check-in nudge */}
      {!checkedInToday && (
        <div className="flex items-center justify-between rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <ClipboardCheck className="h-4 w-4 text-primary" />
            </div>
            <p className="text-sm font-medium text-foreground">You haven't submitted your check-in today.</p>
          </div>
          <Link
            to="/check-in"
            className="ml-4 shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Submit now
          </Link>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Active Tasks"
          value={activeTasks.length}
          icon={<CheckSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />}
          iconBg="bg-blue-50 dark:bg-blue-950/40"
          sub={`${totalPoints} pts total`}
        />
        <StatCard
          title="Workload"
          value={`${Math.round(pct)}%`}
          icon={<TrendingUp className="h-5 w-5 text-violet-600 dark:text-violet-400" />}
          iconBg="bg-violet-50 dark:bg-violet-950/40"
          sub={`${totalPoints} / ${baseline} pts`}
          accent={pct >= 130 ? 'red' : pct >= 100 ? 'yellow' : undefined}
        />
        <StatCard
          title="Blocked"
          value={blockedTasks.length}
          icon={<AlertTriangle className={cn('h-5 w-5', blockedTasks.length > 0 ? 'text-red-500' : 'text-muted-foreground')} />}
          iconBg={blockedTasks.length > 0 ? 'bg-red-50 dark:bg-red-950/40' : 'bg-muted'}
          sub={blockedTasks.length > 0 ? 'Need attention' : 'All clear'}
          accent={blockedTasks.length > 0 ? 'red' : undefined}
        />
        <StatCard
          title="Overwork Signals"
          value={signalCount}
          icon={<Zap className={cn('h-5 w-5', overworked ? 'text-amber-500' : 'text-muted-foreground')} />}
          iconBg={overworked ? 'bg-amber-50 dark:bg-amber-950/40' : 'bg-muted'}
          sub={overworked ? 'Overwork flagged' : 'Within limits'}
          accent={overworked ? 'yellow' : undefined}
        />
      </div>

      {/* Workload bar */}
      <Card>
        <CardContent className="p-5">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">Workload capacity</p>
            <span className={cn(
              'text-sm font-semibold tabular-nums',
              pct >= 130 ? 'text-red-600' : pct >= 100 ? 'text-amber-600' : 'text-foreground',
            )}>{Math.round(pct)}%</span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-700',
                pct >= 130 ? 'bg-red-500' : pct >= 100 ? 'bg-amber-400' : 'bg-primary',
              )}
              style={{ width: `${Math.min(pct, 100)}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{totalPoints} of {baseline} pts used</p>
        </CardContent>
      </Card>

      {/* Overwork signals */}
      {signals && signalCount > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-yellow-800">
              <AlertTriangle className="h-4 w-4" />
              Overwork signals detected
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="space-y-1 text-xs text-yellow-700">
              {signals.loadVsBaseline.tripped  && <li>• {signals.loadVsBaseline.reason}</li>}
              {signals.concurrent.tripped      && <li>• {signals.concurrent.reason}</li>}
              {signals.staleInProgress.tripped && <li>• {signals.staleInProgress.reason}</li>}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Active tasks */}
      <Card>
        <CardHeader className="border-b pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium">My tasks</CardTitle>
            <Link to="/tasks" className="text-xs text-primary hover:underline">View all</Link>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {activeTasks.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-muted-foreground">No active tasks.</p>
          ) : (
            <ul role="list" className="divide-y divide-border">
              {activeTasks.map((task) => {
                const days = daysUntil(task.dueDate);
                return (
                  <li key={task.id} className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-muted/40">
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/tasks/${task.id}`}
                        className="block truncate text-sm font-medium text-foreground hover:text-primary"
                      >
                        {task.title}
                      </Link>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Due {formatDate(task.dueDate)}
                        {days <= 1 && (
                          <span className="ml-1.5 font-medium text-red-600">
                            {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? '· due today' : '· due tomorrow'}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="text-xs text-muted-foreground">{task.points}pt</span>
                      <Badge label={task.status} variant={STATUS_VARIANT[task.status]} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  iconBg?: string;
  sub?: string;
  accent?: 'red' | 'yellow';
}

function StatCard({ title, value, icon, iconBg, sub, accent }: StatCardProps) {
  return (
    <Card className={cn(
      'transition-shadow hover:shadow-md',
      accent === 'red'    ? 'border-red-200 dark:border-red-900/40'    : '',
      accent === 'yellow' ? 'border-amber-200 dark:border-amber-900/40' : '',
    )}>
      <CardContent className="p-5">
        <div className={cn(
          'mb-4 flex h-10 w-10 items-center justify-center rounded-xl',
          iconBg ?? 'bg-muted',
        )}>
          {icon}
        </div>
        <p className={cn(
          'text-3xl font-bold tracking-tight',
          accent === 'red' ? 'text-red-600' : accent === 'yellow' ? 'text-amber-600' : 'text-foreground',
        )}>
          {value}
        </p>
        <p className="mt-1 text-sm font-medium text-muted-foreground">{title}</p>
        {sub && <p className="mt-0.5 text-xs text-muted-foreground/70">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}
